import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Flex,
  Heading,
  Select,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useTheme,
  useToast,
  Badge,
  IconButton,
  Tooltip,
  Text,
} from "@chakra-ui/react";
import { FaDownload, FaSync, FaTrash } from "react-icons/fa";
import { useSupplierRetailer } from "../../../wrappers/SupplierRetailerContext";

// Masters-only screen. Every list is scoped to the organisation toggle in the
// top bar (2026-09-11): Gateway shows only the files that arrived through the
// Gateway FTP drop (or were attributed to Gateway suppliers); SpearMgmt shows
// the rest. The backend decides per file — services/ediOrigin.js.
const PnpFtp = () => {
  const [activeTab, setActiveTab] = useState("processed");
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState("");
  const [history, setHistory] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [historyFolders, setHistoryFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState("");
  const [historyFiles, setHistoryFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reprocessing, setReprocessing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ suppliers: [], filetypes: [] });
  const [selectedFiletype, setSelectedFiletype] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState("");

  const theme = useTheme();
  const toast = useToast();
  const { selectedOrg } = useSupplierRetailer();
  const orgCode = selectedOrg || "SPEARMGMT";
  const tableBg = useColorModeValue("white", "gray.800");
  const headerBg = useColorModeValue(theme.colors.primary2, theme.colors.primary1);

  useEffect(() => {
    setSelectedBatch("");
    setHistory([]);
    setSelectedSupplier("");
    fetchBatches();
    fetchFilterOptions();
    if (activeTab === "pending") fetchPendingFiles();
    if (activeTab === "files" && selectedFolder) fetchHistoryFiles(selectedFolder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgCode]);

  useEffect(() => {
    if (activeTab === "processed" && selectedBatch) {
      fetchHistory(selectedBatch, selectedFiletype, selectedSupplier);
    } else if (activeTab === "processed" && batches.length > 0 && !selectedBatch) {
      setSelectedBatch(batches[0].batch_id);
    }
  }, [activeTab, selectedBatch, batches, selectedFiletype, selectedSupplier]);

  useEffect(() => {
    if (activeTab === "pending") {
      fetchPendingFiles();
    } else if (activeTab === "files") {
      fetchHistoryFolders();
    } else if (activeTab === "processed") {
      // Reload the cron-run list every time the tab is opened — a run that happened
      // while you were watching Pending must show up without a page reload (2026-09-11).
      fetchBatches();
      fetchFilterOptions();
    }
  }, [activeTab]);

  // Live refresh (2026-09-11): the sweep moves files every minute and the EDI cron
  // runs hourly, so whichever tab is open re-reads its list every 30 s — no reload.
  // Skipped while the tab is hidden or an action (reprocess/delete) is running.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.hidden || reprocessing || deleting) return;
      if (activeTab === "pending") fetchPendingFiles(true);
      else if (activeTab === "files" && selectedFolder) fetchHistoryFiles(selectedFolder, true);
      else if (activeTab === "processed") {
        fetchBatches(true);
        if (selectedBatch) fetchHistory(selectedBatch, selectedFiletype, selectedSupplier, true);
      }
    }, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedFolder, selectedBatch, selectedFiletype, selectedSupplier, orgCode, reprocessing, deleting]);

  useEffect(() => {
    if (activeTab === "files" && selectedFolder) {
      fetchHistoryFiles(selectedFolder);
    }
  }, [selectedFolder, activeTab]);

  const fetchBatches = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/pnp-ftp/batches`, { limit: 50, org_code: orgCode });
      if (response.data.status === 1) {
        setBatches(response.data.data);
        if (response.data.data.length > 0) {
          setSelectedBatch((cur) => (silent && cur ? cur : response.data.data[0].batch_id));
        }
      }
    } catch (error) {
      toast({
        title: "Error fetching batches",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async (batchId, filetype = null, supplier = null, silent = false) => {
    try {
      if (!silent) setLoading(true);
      const payload = { batchId, limit: 100, org_code: orgCode };
      if (filetype) payload.filetype = filetype;
      if (supplier) payload.supplier = supplier;
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/pnp-ftp/history`, payload);
      if (response.data.status === 1) {
        setHistory(response.data.data);
      }
    } catch (error) {
      toast({
        title: "Error fetching history",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/pnp-ftp/filter-options`, { org_code: orgCode });
      if (response.data.status === 1) {
        setFilterOptions(response.data.data);
      }
    } catch (error) {
      console.log("Error fetching filter options:", error);
    }
  };

  const fetchPendingFiles = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/pnp-ftp/pending`, { org_code: orgCode });
      if (response.data.status === 1) {
        setPendingFiles(response.data.data);
      }
    } catch (error) {
      toast({
        title: "Error fetching pending files",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchHistoryFolders = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/pnp-ftp/history-folders`);
      if (response.data.status === 1) {
        setHistoryFolders(response.data.data);
        if (response.data.data.length > 0 && !selectedFolder) {
          setSelectedFolder(response.data.data[0]);
        }
      }
    } catch (error) {
      toast({
        title: "Error fetching history folders",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchHistoryFiles = async (folder, silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/pnp-ftp/history-files`, { monthFolder: folder, org_code: orgCode });
      if (response.data.status === 1) {
        setHistoryFiles(response.data.data);
      }
    } catch (error) {
      toast({
        title: "Error fetching history files",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (type, filename, folder = null) => {
    let url = `${process.env.REACT_APP_API_URL}/pnp-ftp/download/${type}/${encodeURIComponent(filename)}`;
    if (folder) {
      url = `${process.env.REACT_APP_API_URL}/pnp-ftp/download/${type}/${encodeURIComponent(folder)}/${encodeURIComponent(filename)}`;
    }
    // window.open cannot send an Authorization header — pass the token as a query
    // param, which requireAuth accepts as a fallback for downloads.
    const token = localStorage.getItem("accessToken");
    if (token) url += `?access_token=${encodeURIComponent(token)}`;
    window.open(url, "_blank");
  };

  const handleReprocess = async (filename) => {
    try {
      setReprocessing(filename);
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/pnp-ftp/reprocess`, { filename });
      if (response.data.status === 1) {
        toast({
          title: "File reprocessed successfully",
          description: `Inserted ${response.data.rowsInserted} rows`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        fetchPendingFiles();
        fetchBatches();
      } else {
        toast({
          title: "Reprocess failed",
          description: response.data.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error reprocessing file",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setReprocessing(null);
    }
  };

  const handleDeleteRecord = async (id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;
    try {
      setDeleting(`record-${id}`);
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/pnp-ftp/delete-record`, { id });
      if (response.data.status === 1) {
        toast({
          title: "Record deleted",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
        fetchHistory(selectedBatch, selectedFiletype, selectedSupplier);
        fetchBatches();
      } else {
        toast({
          title: "Delete failed",
          description: response.data.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error deleting record",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setDeleting(null);
    }
  };

  const handleDeletePendingFile = async (filename) => {
    if (!window.confirm(`Are you sure you want to delete ${filename}?`)) return;
    try {
      setDeleting(`pending-${filename}`);
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/pnp-ftp/delete-pending`, { filename });
      if (response.data.status === 1) {
        toast({
          title: "File deleted",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
        fetchPendingFiles();
      } else {
        toast({
          title: "Delete failed",
          description: response.data.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error deleting file",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteHistoryFile = async (filename, folder) => {
    if (!window.confirm(`Are you sure you want to delete ${filename}?`)) return;
    try {
      setDeleting(`history-${filename}`);
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/pnp-ftp/delete-history`, { filename, folder });
      if (response.data.status === 1) {
        toast({
          title: "File deleted",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
        fetchHistoryFiles(selectedFolder);
      } else {
        toast({
          title: "Delete failed",
          description: response.data.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error deleting file",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = months[date.getMonth()];
    const year = date.getFullYear().toString().slice(-2);
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${day} ${month} '${year} ${hours}:${minutes}`;
  };

  const formatBatchOption = (batch) => {
    const date = formatDate(batch.cron_datetime);
    return `${date} (${batch.success_count} ok, ${batch.failed_count} failed)`;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const renderProcessedTab = () => (
    <Box>
      <Flex mb={4} align="center" gap={4} wrap="wrap">
        <Text fontWeight="bold">Cron Run:</Text>
        <Select
          value={selectedBatch}
          onChange={(e) => setSelectedBatch(e.target.value)}
          maxW="400px"
          bg={tableBg}
        >
          {batches.map((batch) => (
            <option key={batch.batch_id} value={batch.batch_id}>
              {formatBatchOption(batch)}
            </option>
          ))}
        </Select>
        <Text fontWeight="bold" ml={4}>Type:</Text>
        <Select
          value={selectedFiletype}
          onChange={(e) => setSelectedFiletype(e.target.value)}
          maxW="140px"
          bg={tableBg}
          placeholder="All"
        >
          {filterOptions.filetypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </Select>
        <Text fontWeight="bold">Supplier:</Text>
        <Select
          value={selectedSupplier}
          onChange={(e) => setSelectedSupplier(e.target.value)}
          maxW="200px"
          bg={tableBg}
          placeholder="All"
        >
          {filterOptions.suppliers.map((sup) => (
            <option key={sup} value={sup}>
              {sup}
            </option>
          ))}
        </Select>
        <IconButton
          icon={<FaSync />}
          onClick={fetchBatches}
          aria-label="Refresh"
          size="sm"
        />
      </Flex>

      <Box maxH="500px" overflowY="auto">
        <Table variant="striped" bg={tableBg} size="sm">
          <Thead bg={headerBg} position="sticky" top={0} zIndex={1}>
            <Tr>
              <Th color="white">Filename</Th>
              <Th color="white">Type</Th>
              <Th color="white">Supplier</Th>
              <Th color="white">Status</Th>
              <Th color="white">Rows</Th>
              <Th color="white">Error</Th>
              <Th color="white">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {history.map((record, index) => (
              <Tr key={index}>
                <Td fontSize="sm">{record.filename}</Td>
                <Td>
                  <Badge colorScheme={getTypeColor(record.filetype)}>{record.filetype}</Badge>
                </Td>
                <Td fontSize="sm">{record.supplier || "-"}</Td>
                <Td>
                  <Badge colorScheme={record.status === "success" ? "green" : "red"}>
                    {record.status}
                  </Badge>
                </Td>
                <Td>{record.rows_inserted}</Td>
                <Td fontSize="xs" maxW="200px" isTruncated title={record.error_message}>
                  {record.error_message || "-"}
                </Td>
                <Td>
                  <Stack direction="row" spacing={2}>
                    <Tooltip label={record.fileExists ? "Download file" : "File not found"}>
                      <IconButton
                        icon={<FaDownload />}
                        size="xs"
                        onClick={() => handleDownload("history", record.filename, record.historyFolder)}
                        isDisabled={!record.fileExists}
                        aria-label="Download"
                      />
                    </Tooltip>
                    <Tooltip label="Delete record">
                      <IconButton
                        icon={<FaTrash />}
                        size="xs"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => handleDeleteRecord(record.id)}
                        isLoading={deleting === `record-${record.id}`}
                        aria-label="Delete"
                      />
                    </Tooltip>
                  </Stack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      {history.length === 0 && !loading && (
        <Text textAlign="center" mt={4} color="gray.500">
          No records found for this batch
        </Text>
      )}
    </Box>
  );

  const renderPendingTab = () => (
    <Box>
      <Flex mb={4} justify="space-between" align="center">
        <Text fontWeight="bold">Pending Files (ftp_edi folder)</Text>
        <IconButton icon={<FaSync />} onClick={fetchPendingFiles} aria-label="Refresh" size="sm" />
      </Flex>

      <Box maxH="500px" overflowY="auto">
        <Table variant="striped" bg={tableBg} size="sm">
          <Thead bg={headerBg} position="sticky" top={0} zIndex={1}>
            <Tr>
              <Th color="white">Filename</Th>
              <Th color="white">Size</Th>
              <Th color="white">Modified</Th>
              <Th color="white">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {pendingFiles.map((file, index) => (
              <Tr key={index}>
                <Td fontSize="sm">{file.filename}</Td>
                <Td>{formatFileSize(file.size)}</Td>
                <Td>{formatDate(file.modified)}</Td>
                <Td>
                  <Stack direction="row" spacing={2}>
                    <Tooltip label="Download">
                      <IconButton
                        icon={<FaDownload />}
                        size="xs"
                        onClick={() => handleDownload("pending", file.filename)}
                        aria-label="Download"
                      />
                    </Tooltip>
                    {file.isCsv && (
                      <Tooltip label="Reprocess">
                        <IconButton
                          icon={<FaSync />}
                          size="xs"
                          colorScheme="blue"
                          onClick={() => handleReprocess(file.filename)}
                          isLoading={reprocessing === file.filename}
                          aria-label="Reprocess"
                        />
                      </Tooltip>
                    )}
                    <Tooltip label="Delete file">
                      <IconButton
                        icon={<FaTrash />}
                        size="xs"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => handleDeletePendingFile(file.filename)}
                        isLoading={deleting === `pending-${file.filename}`}
                        aria-label="Delete"
                      />
                    </Tooltip>
                  </Stack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      {pendingFiles.length === 0 && !loading && (
        <Text textAlign="center" mt={4} color="gray.500">
          No pending files
        </Text>
      )}
    </Box>
  );

  const renderFilesTab = () => (
    <Box>
      <Flex mb={4} align="center" gap={4}>
        <Text fontWeight="bold">History Folder:</Text>
        <Select
          value={selectedFolder}
          onChange={(e) => setSelectedFolder(e.target.value)}
          maxW="200px"
          bg={tableBg}
        >
          {historyFolders.map((folder) => (
            <option key={folder} value={folder}>
              {folder}
            </option>
          ))}
        </Select>
        <IconButton icon={<FaSync />} onClick={fetchHistoryFolders} aria-label="Refresh" size="sm" />
      </Flex>

      <Box maxH="500px" overflowY="auto">
        <Table variant="striped" bg={tableBg} size="sm">
          <Thead bg={headerBg} position="sticky" top={0} zIndex={1}>
            <Tr>
              <Th color="white">Filename</Th>
              <Th color="white">Size</Th>
              <Th color="white">Modified</Th>
              <Th color="white">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {historyFiles.map((file, index) => (
              <Tr key={index}>
                <Td fontSize="sm">{file.filename}</Td>
                <Td>{formatFileSize(file.size)}</Td>
                <Td>{formatDate(file.modified)}</Td>
                <Td>
                  <Stack direction="row" spacing={2}>
                    <Tooltip label="Download">
                      <IconButton
                        icon={<FaDownload />}
                        size="xs"
                        onClick={() => handleDownload("history", file.filename, file.folder)}
                        aria-label="Download"
                      />
                    </Tooltip>
                    <Tooltip label="Delete file">
                      <IconButton
                        icon={<FaTrash />}
                        size="xs"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => handleDeleteHistoryFile(file.filename, file.folder)}
                        isLoading={deleting === `history-${file.filename}`}
                        aria-label="Delete"
                      />
                    </Tooltip>
                  </Stack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      {historyFiles.length === 0 && !loading && (
        <Text textAlign="center" mt={4} color="gray.500">
          No files in this folder
        </Text>
      )}
    </Box>
  );

  const getTypeColor = (type) => {
    switch (type) {
      case "sales":
        return "blue";
      case "sdc":
        return "green";
      case "strikerate":
        return "purple";
      case "articles":
        return "orange";
      default:
        return "gray";
    }
  };

  return (
    <Box mt={16} px={4}>
      <Heading size="md" textAlign="center" mb={4}>
        PNP FTP Monitor
      </Heading>
      <Text textAlign="center" fontSize="sm" color="gray.500" mb={3}>
        Showing <b>{orgCode}</b> files only — switch organisation in the top bar to see another organisation's EDI files.
      </Text>

      <Flex justify="center" mb={4}>
        <Stack direction="row" spacing={4}>
          <Button
            onClick={() => setActiveTab("processed")}
            variant={activeTab === "processed" ? "solid" : "outline"}
            colorScheme={activeTab === "processed" ? "blue" : "gray"}
          >
            Processed Files
          </Button>
          <Button
            onClick={() => setActiveTab("pending")}
            variant={activeTab === "pending" ? "solid" : "outline"}
            colorScheme={activeTab === "pending" ? "blue" : "gray"}
          >
            Pending Files
          </Button>
          <Button
            onClick={() => setActiveTab("files")}
            variant={activeTab === "files" ? "solid" : "outline"}
            colorScheme={activeTab === "files" ? "blue" : "gray"}
          >
            History Files
          </Button>
        </Stack>
      </Flex>

      <Flex justifyContent="center">
        <Box width="1100px">
          {loading ? (
            <Flex justify="center" mt={8}>
              <Spinner size="xl" />
            </Flex>
          ) : (
            <>
              {activeTab === "processed" && renderProcessedTab()}
              {activeTab === "pending" && renderPendingTab()}
              {activeTab === "files" && renderFilesTab()}
            </>
          )}
        </Box>
      </Flex>
    </Box>
  );
};

export default PnpFtp;
